'use client';

import { c as _c } from "react/compiler-runtime";
import type { PrV2CommentResponse, PrV2ReviewThread } from '@/lib/pr-v2/types';
import { PatchDiff } from '@pierre/diffs/react';
import { useMemo } from 'react';
import { truncateDiffHunk } from './lib/truncate-diff-hunk';
import { PrV2FilePathLabel } from './shared/pr-v2-file-path-label';
import { PIERRE_COMPACT_DIFF_PALETTE_OVERRIDES, PIERRE_UNIFIED_DIFF_OPTIONS } from '@/components/shared/diff/pierre-diff-palette';
import { RenderErrorBoundary } from '@/components/shared/render-error-boundary';
import { BetterTooltip } from '@/components/ui/tooltip';
import { copyToClipboard } from '@/utils/clipboard';
type PrV2ThreadCodePreviewProps = {
  comment: PrV2CommentResponse;
  thread?: PrV2ReviewThread;
};
type PreviewData = {
  path: string;
  patch: string;
  referenceText: string;
};
function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}
function getPreviewLineNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : null;
}
function asGithubDiffSide(value: unknown): 'LEFT' | 'RIGHT' | null {
  return value === 'LEFT' || value === 'RIGHT' ? value : null;
}
function resolveRangeEndSide(raw: Record<string, unknown> | null, thread: PrV2ReviewThread | undefined): 'LEFT' | 'RIGHT' {
  return asGithubDiffSide(raw?.side ?? raw?.original_side) ?? thread?.diffSide ?? 'RIGHT';
}
function resolveRangeStartSide(raw: Record<string, unknown> | null, thread: PrV2ReviewThread | undefined, endSide: 'LEFT' | 'RIGHT'): 'LEFT' | 'RIGHT' {
  return asGithubDiffSide(raw?.start_side) ?? thread?.startDiffSide ?? endSide;
}
function pickPreferredLineNumber(side: 'LEFT' | 'RIGHT', preferredCandidates: Array<number | null>, fallbackCandidates: Array<number | null>): number | null {
  const sideAwareCandidates = side === 'LEFT' ? [...preferredCandidates, ...fallbackCandidates] : [...fallbackCandidates, ...preferredCandidates];
  return sideAwareCandidates.find(value => value != null) ?? null;
}
function formatReferenceText(path: string, comment: PrV2CommentResponse, thread: PrV2ReviewThread | undefined): string {
  const raw = asRecord(comment.raw);
  const endSide = resolveRangeEndSide(raw, thread);
  const startSide = resolveRangeStartSide(raw, thread, endSide);
  const endLine = pickPreferredLineNumber(endSide, [getPreviewLineNumber(raw?.original_line), getPreviewLineNumber(thread?.originalLine)], [getPreviewLineNumber(raw?.line), getPreviewLineNumber(thread?.line), getPreviewLineNumber(comment.line)]);
  const startLine = pickPreferredLineNumber(startSide, [getPreviewLineNumber(raw?.original_start_line), getPreviewLineNumber(thread?.originalStartLine)], [getPreviewLineNumber(raw?.start_line), getPreviewLineNumber(thread?.startLine)]) ?? endLine;
  if (startLine == null && endLine == null) {
    return path;
  }
  const normalizedStartLine = startLine != null && endLine != null ? Math.min(startLine, endLine) : startLine ?? endLine;
  const normalizedEndLine = startLine != null && endLine != null ? Math.max(startLine, endLine) : endLine ?? startLine;
  if (normalizedStartLine == null || normalizedEndLine == null || normalizedStartLine === normalizedEndLine) {
    return `${path}:${normalizedStartLine ?? normalizedEndLine}`;
  }
  return `${path}:${normalizedStartLine}-${normalizedEndLine}`;
}
function normalizePatch(path: string, diffHunk: string): string {
  const trimmed = diffHunk.trim();
  if (trimmed.startsWith('diff --git ')) {
    return trimmed;
  }
  return [`diff --git a/${path} b/${path}`, `--- a/${path}`, `+++ b/${path}`, trimmed].join('\n');
}
function buildPreviewData(comment: PrV2CommentResponse, thread: PrV2ReviewThread | undefined): PreviewData | null {
  if (comment.commentType !== 'review_comment') {
    return null;
  }
  const path = comment.path ?? thread?.path ?? null;
  if (!path) {
    return null;
  }
  const raw = asRecord(comment.raw);
  const diffHunk = typeof raw?.diff_hunk === 'string' ? raw.diff_hunk.trim() : '';
  if (!diffHunk) {
    return null;
  }
  return {
    path,
    patch: normalizePatch(path, truncateDiffHunk(diffHunk)),
    referenceText: formatReferenceText(path, comment, thread)
  };
}
function PatchTextFallback(t0) {
  const $ = _c(2);
  const {
    patch
  } = t0;
  let t1;
  if ($[0] !== patch) {
    t1 = <pre data-testid="pr-v2-thread-code-preview-fallback" className="overflow-x-auto border-t border-zinc-700/40 px-3 py-2 text-[13px] leading-[18px] text-zinc-300">{patch}</pre>;
    $[0] = patch;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export function PrV2ThreadCodePreview(t0) {
  const $ = _c(16);
  const {
    comment,
    thread
  } = t0;
  let t1;
  if ($[0] !== comment || $[1] !== thread) {
    t1 = buildPreviewData(comment, thread);
    $[0] = comment;
    $[1] = thread;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const previewData = t1;
  if (!previewData) {
    return null;
  }
  let t2;
  if ($[3] !== previewData.referenceText) {
    t2 = () => void copyToClipboard(previewData.referenceText, {
      successMessage: "File reference copied",
      errorMessage: "Failed to copy file reference"
    });
    $[3] = previewData.referenceText;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== previewData.path || $[6] !== previewData.referenceText) {
    t3 = <PrV2FilePathLabel filename={previewData.path} title={previewData.referenceText} className="inline-flex max-w-full gap-1.5" basenameClassName="text-[11px] text-zinc-400 transition-colors group-hover:text-zinc-200" directoryClassName="text-[11px] text-zinc-500 transition-colors group-hover:text-zinc-300" />;
    $[5] = previewData.path;
    $[6] = previewData.referenceText;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t2 || $[9] !== t3) {
    t4 = <div className="border-b border-zinc-700/40 px-3 py-2"><BetterTooltip content="Copy file reference"><button type="button" data-testid="pr-v2-thread-code-preview-reference" className="group max-w-full cursor-pointer text-left transition-colors focus-visible:outline-none" onClick={t2}>{t3}</button></BetterTooltip></div>;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== previewData.patch) {
    t5 = <RenderErrorBoundary fallback={<PatchTextFallback patch={previewData.patch} />}><PatchDiff className="block w-full" patch={previewData.patch} options={PIERRE_UNIFIED_DIFF_OPTIONS} style={PIERRE_COMPACT_DIFF_PALETTE_OVERRIDES} /></RenderErrorBoundary>;
    $[11] = previewData.patch;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t4 || $[14] !== t5) {
    t6 = <div data-testid="pr-v2-thread-code-preview" className="mt-3 overflow-hidden rounded-md border border-zinc-700/50 bg-black/40">{t4}{t5}</div>;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}